<script setup lang="ts">
import { onBeforeMount, reactive, ref, watch } from 'vue'

import BetaProfileIntegrationCard from '@/components/beta-dashboard/molecules/BetaProfileIntegrationCard.vue'
import BetaSocialLinkCard from '@/components/beta-dashboard/molecules/BetaSocialLinkCard.vue'
import BetaGlobalScriptsPanel from '@/components/beta-dashboard/organisms/BetaGlobalScriptsPanel.vue'
import BetaProfileIntegrationDialog from '@/components/beta-dashboard/organisms/BetaProfileIntegrationDialog.vue'
import BetaSocialLinkDialog from '@/components/beta-dashboard/organisms/BetaSocialLinkDialog.vue'
import BetaVimeoGuideDialog from '@/components/beta-dashboard/organisms/BetaVimeoGuideDialog.vue'
import modal from '@/services/modal'
import notification, { type ToastId } from '@/services/notification'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import type {
    BetaIntegrationCodes,
    BetaProfileIntegration,
    BetaProfileIntegrationProvider,
    BetaSocialLink,
    BetaSocialLinkInput,
} from '@/types/beta-dashboard'

const betaStore = useBetaDashboardStore()

const codes = reactive<BetaIntegrationCodes>({
    pixelId: '',
    analyticsId: '',
    analyticsIdV4: '',
    vimeoToken: '',
})

const profileDialogOpen = ref(false)
const profileProvider = ref<BetaProfileIntegrationProvider>('insta')
const profileResult = ref<BetaProfileIntegration | null>(null)
const profileError = ref('')
const searchingProfile = ref(false)
const connectingProfile = ref(false)
const busyProfiles = ref<BetaProfileIntegrationProvider[]>([])

const socialDialogOpen = ref(false)
const socialLinkForEdit = ref<BetaSocialLink | null>(null)
const savingSocialLink = ref(false)
const deletingSocialLinkIds = ref<number[]>([])
const savingCodes = ref(false)
const vimeoGuideOpen = ref(false)

watch(
    () => betaStore.integrationCodes,
    (value) => Object.assign(codes, JSON.parse(JSON.stringify(value))),
    { deep: true, immediate: true },
)

onBeforeMount(() => betaStore.loadIntegrations())

const openProfileDialog = (provider: BetaProfileIntegrationProvider) => {
    profileProvider.value = provider
    profileResult.value = null
    profileError.value = ''
    profileDialogOpen.value = true
}

const closeProfileDialog = () => {
    if (searchingProfile.value || connectingProfile.value) return
    profileDialogOpen.value = false
    profileResult.value = null
    profileError.value = ''
}

const searchProfile = async (provider: BetaProfileIntegrationProvider, username: string) => {
    if (searchingProfile.value) return

    searchingProfile.value = true
    profileError.value = ''
    try {
        const profile = await betaStore.findIntegrationProfile(provider, username)
        if (profile.private) {
            profileError.value = 'Este perfil é privado. Altere a privacidade e tente novamente.'
            return
        }
        profileResult.value = profile
    } catch (error) {
        profileError.value = error instanceof Error ? error.message : 'Não encontramos esse perfil.'
    } finally {
        searchingProfile.value = false
    }
}

const connectProfile = async (provider: BetaProfileIntegrationProvider, username: string) => {
    if (connectingProfile.value) return

    connectingProfile.value = true
    const toastId: ToastId = notification.loading('Realizando a integração…')
    try {
        await betaStore.connectIntegrationProfile(provider, username)
        notification.update(toastId, 'Integração realizada com sucesso.', 'success')
        profileDialogOpen.value = false
        profileResult.value = null
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível realizar a integração.',
            'error',
        )
    } finally {
        connectingProfile.value = false
    }
}

const disconnectProfile = async (provider: BetaProfileIntegrationProvider) => {
    const label = provider === 'insta' ? 'Instagram' : 'YouTube'
    const confirmed = await modal.confirmation(
        'Remover integração?',
        `O perfil do ${label} será desconectado deste site.`,
        {
            type: 'danger',
            textConfirm: 'Sim, remover',
            textCancel: 'Cancelar',
        },
    )
    if (!confirmed || busyProfiles.value.includes(provider)) return

    busyProfiles.value = [...busyProfiles.value, provider]
    const toastId: ToastId = notification.loading('Removendo integração…')
    try {
        await betaStore.disconnectIntegrationProfile(provider)
        notification.update(toastId, 'Integração removida com sucesso.', 'success')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível remover a integração.',
            'error',
        )
    } finally {
        busyProfiles.value = busyProfiles.value.filter((item) => item !== provider)
    }
}

const openSocialLinkDialog = (link: BetaSocialLink | null = null) => {
    socialLinkForEdit.value = link
    socialDialogOpen.value = true
}

const closeSocialLinkDialog = () => {
    if (savingSocialLink.value) return
    socialDialogOpen.value = false
    socialLinkForEdit.value = null
}

const saveSocialLink = async (link: BetaSocialLinkInput) => {
    if (savingSocialLink.value) return

    savingSocialLink.value = true
    const editing = Boolean(link.id)
    const toastId: ToastId = notification.loading(
        editing ? 'Atualizando rede social…' : 'Adicionando rede social…',
    )
    try {
        if (editing) await betaStore.editSocialLink(link)
        else await betaStore.addSocialLink(link)

        notification.update(
            toastId,
            editing ? 'Rede social atualizada com sucesso.' : 'Rede social adicionada com sucesso.',
            'success',
        )
        socialDialogOpen.value = false
        socialLinkForEdit.value = null
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível salvar a rede social.',
            'error',
        )
    } finally {
        savingSocialLink.value = false
    }
}

const removeSocialLink = async (link: BetaSocialLink) => {
    const confirmed = await modal.confirmation(
        'Excluir rede social?',
        `O endereço “${link.link}” será removido do site.`,
        {
            type: 'danger',
            textConfirm: 'Sim, excluir',
            textCancel: 'Cancelar',
        },
    )
    if (!confirmed || deletingSocialLinkIds.value.includes(link.id)) return

    deletingSocialLinkIds.value = [...deletingSocialLinkIds.value, link.id]
    const toastId: ToastId = notification.loading('Excluindo rede social…')
    try {
        await betaStore.removeSocialLink(link.id)
        notification.update(toastId, 'Rede social excluída com sucesso.', 'success')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível excluir a rede social.',
            'error',
        )
    } finally {
        deletingSocialLinkIds.value = deletingSocialLinkIds.value.filter((id) => id !== link.id)
    }
}

const saveCodes = async () => {
    if (savingCodes.value) return

    savingCodes.value = true
    const toastId: ToastId = notification.loading('Atualizando códigos…')
    try {
        await betaStore.saveIntegrationCodes(JSON.parse(JSON.stringify(codes)))
        notification.update(toastId, 'Códigos atualizados com sucesso.', 'success')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível atualizar os códigos.',
            'error',
        )
    } finally {
        savingCodes.value = false
    }
}
</script>

<template>
    <div class="beta-integrations">
        <div class="beta-integrations__heading">
            <div>
                <p>Integrações</p>
                <h2>Conecte seus canais</h2>
                <span>Importe conteúdos, adicione redes sociais e configure ferramentas de medição.</span>
            </div>
        </div>

        <div v-if="betaStore.loadingIntegrations" class="beta-integrations__state">
            Carregando integrações…
        </div>

        <div v-else-if="betaStore.integrationsError" class="beta-integrations__state is-error">
            <span>{{ betaStore.integrationsError }}</span>
            <button type="button" @click="betaStore.loadIntegrations()">Tentar novamente</button>
        </div>

        <template v-else>
            <section class="beta-integrations__section">
                <div class="beta-integrations__section-heading">
                    <div>
                        <h3>Conteúdo conectado</h3>
                        <p>Use seus perfis para importar conteúdos do Instagram e YouTube.</p>
                    </div>
                </div>

                <div class="beta-integrations__profiles">
                    <BetaProfileIntegrationCard
                        provider="insta"
                        :profile="betaStore.connectedProfiles.insta"
                        :busy="busyProfiles.includes('insta')"
                        @connect="openProfileDialog"
                        @disconnect="disconnectProfile"
                    />
                    <BetaProfileIntegrationCard
                        provider="youtube"
                        :profile="betaStore.connectedProfiles.youtube"
                        :busy="busyProfiles.includes('youtube')"
                        @connect="openProfileDialog"
                        @disconnect="disconnectProfile"
                    />
                </div>
            </section>

            <section class="beta-integrations__section">
                <div class="beta-integrations__section-heading is-action">
                    <div>
                        <h3>Redes sociais</h3>
                        <p>Cadastre os endereços exibidos no seu site.</p>
                    </div>
                    <button type="button" @click="openSocialLinkDialog()">+ Adicionar</button>
                </div>

                <div v-if="betaStore.socialLinks.length" class="beta-integrations__social-list">
                    <BetaSocialLinkCard
                        v-for="link in betaStore.socialLinks"
                        :key="link.id"
                        :link="link"
                        :deleting="deletingSocialLinkIds.includes(link.id)"
                        @edit="openSocialLinkDialog"
                        @remove="removeSocialLink"
                    />
                </div>
                <div v-else class="beta-integrations__empty">
                    Nenhuma rede social cadastrada.
                </div>
            </section>

            <form class="beta-integrations__section" @submit.prevent="saveCodes">
                <div class="beta-integrations__section-heading">
                    <div>
                        <h3>Códigos e acompanhamento</h3>
                        <p>Use os mesmos identificadores configurados no painel anterior.</p>
                    </div>
                </div>

                <div class="beta-integrations__fields">
                    <label>
                        <span>Google Analytics</span>
                        <input v-model="codes.analyticsId" type="text" placeholder="AA-000000000-0">
                    </label>
                    <label>
                        <span>Google Analytics V4</span>
                        <input v-model="codes.analyticsIdV4" type="text" placeholder="A-AAAAAAAAAA">
                    </label>
                    <label>
                        <span>Facebook Pixel</span>
                        <input v-model="codes.pixelId" type="text" placeholder="0000000000000000">
                    </label>
                    <label>
                        <span>
                            Vimeo Token
                            <button type="button" @click="vimeoGuideOpen = true">Como gerar?</button>
                        </span>
                        <input v-model="codes.vimeoToken" type="text" placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx">
                    </label>
                </div>

                <div class="beta-integrations__form-actions">
                    <button type="submit" :disabled="savingCodes">
                        {{ savingCodes ? 'Salvando…' : 'Salvar códigos' }}
                    </button>
                </div>
            </form>
        </template>

        <BetaGlobalScriptsPanel />

        <BetaProfileIntegrationDialog
            :open="profileDialogOpen"
            :provider="profileProvider"
            :profile="profileResult"
            :searching="searchingProfile"
            :connecting="connectingProfile"
            :error="profileError"
            @close="closeProfileDialog"
            @search="searchProfile"
            @connect="connectProfile"
            @reset="profileResult = null; profileError = ''"
        />

        <BetaSocialLinkDialog
            :open="socialDialogOpen"
            :integration="socialLinkForEdit"
            :saving="savingSocialLink"
            @close="closeSocialLinkDialog"
            @save="saveSocialLink"
        />

        <BetaVimeoGuideDialog :open="vimeoGuideOpen" @close="vimeoGuideOpen = false" />
    </div>
</template>

<style scoped>
.beta-integrations {
    display: grid;
    gap: 14px;
}

.beta-integrations__heading {
    padding-bottom: 15px;
    border-bottom: 1px solid #edf1f6;
}

.beta-integrations__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-integrations__heading h2 {
    margin-top: 3px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 850;
    letter-spacing: -0.025em;
}

.beta-integrations__heading span {
    display: block;
    margin-top: 5px;
    color: #7d8da3;
    font-size: 11px;
}

.beta-integrations__section {
    border: 1px solid #e2e8f0;
    border-radius: 17px;
    background: #fbfcfe;
    padding: 14px;
}

.beta-integrations__section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 11px;
}

.beta-integrations__section-heading h3 {
    color: #304761;
    font-size: 14px;
    font-weight: 850;
}

.beta-integrations__section-heading p {
    margin-top: 3px;
    color: #8391a4;
    font-size: 12px;
}

.beta-integrations__section-heading > button,
.beta-integrations__form-actions button,
.beta-integrations__state button {
    min-height: 34px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 12px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-integrations__profiles,
.beta-integrations__fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.beta-integrations__social-list {
    border-top: 1px solid #edf1f5;
}

.beta-integrations__empty {
    border: 1px dashed #dce4ed;
    border-radius: 12px;
    padding: 14px;
    color: #8291a5;
    font-size: 12px;
    text-align: center;
}

.beta-integrations__fields label {
    display: grid;
    gap: 6px;
}

.beta-integrations__fields label > span {
    color: #40566f;
    font-size: 12px;
    font-weight: 850;
}

.beta-integrations__fields label > span button {
    margin-left: 4px;
    color: var(--main-color);
    font-size: inherit;
    font-weight: 850;
}

.beta-integrations__fields input {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 11px;
    background: #fff;
    padding: 9px 10px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-integrations__fields input:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-integrations__form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
}

.beta-integrations__form-actions button:disabled {
    cursor: wait;
    opacity: 0.55;
}

.beta-integrations__state {
    display: grid;
    min-height: 180px;
    place-content: center;
    justify-items: center;
    gap: 8px;
    border: 1px dashed #dbe3ed;
    border-radius: 17px;
    background: #f9fbfd;
    padding: 20px;
    color: #7e8da2;
    font-size: 12px;
    text-align: center;
}

.beta-integrations__state.is-error {
    color: #a34e4e;
}

@media (max-width: 760px) {
    .beta-integrations__profiles,
    .beta-integrations__fields {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 520px) {
    .beta-integrations__section-heading.is-action {
        align-items: stretch;
        flex-direction: column;
    }
}
</style>
